import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { AlertCircle } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail ? formatApiErrorDetail(detail) : (err.message || 'Network error — unable to reach server. Please check your connection.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src="/finominal-logo.jpg" alt="Finominal GST" className="w-10 h-10 object-contain rounded-sm" />
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Finominal GST
            </h1>
          </div>

          <h2 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome back
          </h2>
          <p className="text-slate-500 mb-8">Enter your credentials to access your account</p>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-sm flex items-center gap-2 text-red-700 text-sm" data-testid="login-error-alert">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@taxledgerpro.com"
                className="mt-1.5 h-10 rounded-sm border-slate-300 focus:border-[#0055FF] focus:ring-[#0055FF]"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1.5 h-10 rounded-sm border-slate-300 focus:border-[#0055FF] focus:ring-[#0055FF]"
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-10 bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm font-medium transition-colors"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner" />
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#0055FF] hover:underline font-medium" data-testid="register-link">
              Create account
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Hero image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/f806aebd-c1cf-49ce-be39-d48ca5018423/images/d8a5b8f4beb77f3c1731edd502f567feb48213c3551394ace6607bcc56e228cf.png"
          alt="Swiss design graphic"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/10" />
      </div>
    </div>
  );
};

export default Login;
